import { type BadgeProps } from '@chakra-ui/react/badge';
import { useFieldContext } from '@chakra-ui/react/field';
import { Flex } from '@chakra-ui/react/flex';
import { chakra, type HTMLChakraProps, type SystemStyleObject, useSlotRecipe } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { cloneElement, forwardRef, isValidElement, type ReactElement, type ReactNode } from 'react';

import { IconInfoCircle } from '../../icons';
import BitkitTooltip from '../BitkitTooltip/BitkitTooltip';

export interface BitkitLabelProps extends Omit<HTMLChakraProps<'label'>, 'children'> {
  badge?: ReactNode;
  counterText?: string;
  disabled?: boolean;
  children?: string;
  optional?: boolean;
  tooltip?: string;
}

const disabledStyles: SystemStyleObject = {
  color: 'text/disabled',
  '& > span': { color: 'text/disabled' },
};

const BitkitLabel = forwardRef<HTMLLabelElement, BitkitLabelProps>((props, ref) => {
  const { badge, counterText, css, disabled: disabledProp, children, optional: optionalProp, tooltip, ...rest } = props;

  const field = useFieldContext();
  const styles = useSlotRecipe({ key: 'field' })();

  const disabled = disabledProp ?? field?.disabled;
  const optional = optionalProp ?? (field ? !field.required : undefined);

  const badgeProps = disabled ? ({ colorVariant: 'neutral', size: 'xs' } as const) : ({ size: 'xs' } as const);
  const renderedBadge = isValidElement(badge) ? cloneElement(badge as ReactElement<BadgeProps>, badgeProps) : badge;

  return (
    <chakra.label
      ref={ref}
      css={[styles.label, disabled && disabledStyles, css]}
      data-disabled={disabled || undefined}
      {...rest}
    >
      {children}{' '}
      {optional && (
        <Text as="span" textStyle="body/md/regular">
          (optional)
        </Text>
      )}
      {!!tooltip && (
        <BitkitTooltip text={tooltip}>
          <IconInfoCircle size="16" color="icon/tertiary" />
        </BitkitTooltip>
      )}
      {(!!counterText || !!badge) && (
        <Flex alignItems="center" gap="12" marginInlineStart="auto">
          {renderedBadge}
          {!!counterText && (
            <Text as="span" color={disabled ? 'text/disabled' : 'text/helper'} textStyle="comp/input/helperText">
              {counterText}
            </Text>
          )}
        </Flex>
      )}
    </chakra.label>
  );
});

BitkitLabel.displayName = 'BitkitLabel';

export default BitkitLabel;
